import { Injector, Optional, SecurityContext, inject, Directive } from '@angular/core';
import {
  DISPLAY_TYPE,
  styler,
  IWidgetConfig,
  Parent,
  provideAsWidgetRef,
  register,
  StylableComponent,
  SanitizePipe
} from '@wm/components-base';
import { setProperty, toggleClass } from '@wm/core';

import {get, isEqual, isObject, isString, startsWith} from 'lodash-es';

const DEFAULT_CLS = 'app-label';
const WIDGET_CONFIG: IWidgetConfig = {
  widgetType: 'wm-label',
  hostClass: DEFAULT_CLS,
  displayType: DISPLAY_TYPE.INLINE_BLOCK
};

@Directive({
  selector: '[wmLabel]',
  standalone: true,
  providers: [
    SanitizePipe,
    provideAsWidgetRef(LabelDirective)
  ],
  exportAs: 'wmLabel'
})
export class LabelDirective extends StylableComponent {
  static initializeProps = register('wm-label');
  private sanitizePipe: SanitizePipe;

  constructor(inj: Injector, @Optional() public _viewParent: Parent) {
    super(inj, WIDGET_CONFIG, _viewParent);
    this.setPipe();
    styler(this.nativeElement, this);
  }

  setPipe() {
   this.sanitizePipe = inject(SanitizePipe);
  }

  override onPropertyChange(key: string, nv: any, ov?: any) {
    if (key === 'caption') {
      // Check for trustPipe safe values
      let bindContent = this.nativeElement.getAttribute('caption.bind');
      let safeValue = bindContent ? nv && bindContent.includes('trustAs:') : false;
      if (isObject(nv) && !safeValue) {
        setProperty(this.nativeElement, 'textContent', JSON.stringify(nv));
      } else if (isObject(nv) && safeValue) {
        setProperty(this.nativeElement, 'innerHTML', get(nv, Object.keys(nv)[0]));
      }  else {
        if (!this.sanitizePipe) {
          this.setPipe();
        }
        if(!startsWith(nv, 'bind') && !isEqual(nv,this.nativeElement.getAttribute('name'))) {
          setProperty(this.nativeElement, 'innerHTML', this.sanitizePipe.transform(nv, SecurityContext.HTML));
        }
        else if(bindContent) {
          setProperty(this.nativeElement, 'innerHTML', this.getWordFromBindCaption("bind:" + bindContent));
        }
        else {
          setProperty(this.nativeElement, 'innerHTML', this.getWordFromBindCaption(this.sanitizePipe.transform(nv, SecurityContext.HTML)));
        }
      }
      super.onPropertyChange(key, nv, ov);
    } else if (key === 'required') {
      toggleClass(this.nativeElement, 'required', nv);
    } else {
      super.onPropertyChange(key, nv, ov);
    }
  }

  /**
   * Extracts the proper name or identifier from bindings such as 'Variables.empData.dataset.name' or 'Widgets.employeeList.dataset.UserId'.
   * The function will return the name or UserId as a small string instead of the full binding path like 'bind:Variables.empData.dataset.name'.
   */
  getWordFromBindCaption(value: any): string {
    let extractedName: string = '';
    /*
       * OBJECT_CHAIN_REGEX is used to extract object chaining from the binding text.
       * For example, if the binding is 'bind:Variables.empData.dataset.name, Widgets.employeeList.dataset.name',
       * it will extract an array like [Variables.empData.dataset.name, Widgets.employeeList.dataset.name].
   */
    const OBJECT_CHAIN_REGEX = /(?:\w+(?:\[\$?\w+\])?\.)+\w+/g;
    /*
       * FUNCTION_NAME_REGEX helps extract function names from the bounded string.
       * This is useful when the binding includes function calls.
    */
    const FUNCTION_NAME_REGEX = /\b(\w+)\s*\(/g;

    if(isString(value) && startsWith(value,"bind")) {
       let ObjectChains  = value.match(OBJECT_CHAIN_REGEX);
       let functionNames = [...value.matchAll(FUNCTION_NAME_REGEX)].map(match => match[1]);
       if(ObjectChains && ObjectChains.length > 0) {
          extractedName = ObjectChains[this.getRandomInt(0, ObjectChains.length-1)].split('.').pop() || '';
       }
       else if(functionNames && functionNames.length > 0) {
          extractedName = functionNames[this.getRandomInt(0, functionNames.length-1)];
       }
       else {
         extractedName =  value.replace('bind:','');
         try {
           extractedName  = eval(extractedName);
         }
         catch(e) {
           return extractedName;
         }
       }
    }
    return (extractedName || value);
  }
  getRandomInt(min: number, max: number): number {
    // Ensure the range is valid and inclusive
    return Math.floor(Math.random() * (max - min + 1)) + min;
  }
}
